import { adminServerSchema, paginated } from '@hearth/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Import, Plus, Search, SlidersHorizontal as SlidersHorizontalIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { GameIcon } from '@/components/game-icon';
import { PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { QueryState } from '@/components/query-state';
import { ServerStatusBadge } from '@/components/status-badges';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiRequest } from '@/lib/api';

export const Route = createFileRoute('/_app/admin/servers/')({
  validateSearch: (s: Record<string, unknown>): { page?: number; search?: string } => ({
    page: Number(s.page) > 1 ? Number(s.page) : undefined,
    search: typeof s.search === 'string' && s.search ? s.search : undefined,
  }),
  component: ServersPage,
});

function ServersPage() {
  const { page = 1, search = '' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [term, setTerm] = useState(search);
  useEffect(() => {
    if (term === search) return;
    const t = setTimeout(() => void navigate({ search: { search: term || undefined } }), 300);
    return () => clearTimeout(t);
  }, [term, search, navigate]);

  const query = useQuery({
    queryKey: ['admin', 'servers', { page, search }],
    queryFn: ({ signal }) =>
      apiRequest(
        `/api/application/servers?page=${page}&perPage=25${search ? `&search=${encodeURIComponent(search)}` : ''}`,
        {
          schema: paginated(adminServerSchema),
          signal,
        },
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Servers"
        icon={SlidersHorizontalIcon}
        description="Every game server on every node."
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/admin/servers/import">
                <Import /> Import
              </Link>
            </Button>
            <Button asChild>
              <Link to="/admin/servers/new">
                <Plus /> New server
              </Link>
            </Button>
          </>
        }
      />
      <div className="relative mb-4 w-full max-w-xs">
        <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
        <Input
          className="pl-8"
          placeholder="Search name, id or owner"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          aria-label="Search servers"
        />
      </div>
      <QueryState query={query}>
        {(data) => (
          <div className="space-y-4">
            <div className="table-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Server</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Node</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Game</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-muted-foreground py-8 text-center">
                        No servers yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {data.data.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <Link
                          to="/admin/servers/$serverId"
                          params={{ serverId: String(s.id) }}
                          className="font-medium hover:underline"
                        >
                          {s.name}
                        </Link>
                        <div className="text-muted-foreground flex items-center gap-2 font-mono text-xs">
                          {s.identifier} <ServerStatusBadge status={s.status} />
                        </div>
                      </TableCell>
                      <TableCell>{s.owner.username}</TableCell>
                      <TableCell>{s.node.name}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {s.allocation.ipAlias ?? s.allocation.ip}:{s.allocation.port}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <GameIcon identity={s.profile.identity} size="sm" />
                          <div className="grid">
                            <span className="text-sm">{s.egg.name}</span>
                            <span className="text-muted-foreground text-xs">{s.nest.name}</span>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Pagination
              {...data.meta}
              onPageChange={(p) => void navigate({ search: (s) => ({ ...s, page: p }) })}
            />
          </div>
        )}
      </QueryState>
    </>
  );
}
